import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import './Pages/PagesCss/NavBar.css';
import logo from '../components/assets/logo.png';

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  const toggleMobile = () => {
    setMobileOpen(!mobileOpen);
    if (!mobileOpen) {
      setMobileSubMenu(null);
    }
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileSubMenu(null);
  };

  const openMobileSubMenu = (index) => setMobileSubMenu(index);
  const closeMobileSubMenu = () => setMobileSubMenu(null);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);

  const handleMobileKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openMobileSubMenu(index);
    } else if (event.key === 'Escape') {
      closeMobileSubMenu();
    }
  };

  const menuItems = [
    { name: "Home", path: "/" },
    {
      name: "Text Style",
      dropdown: [
        {
          sub: "Generators",
          items: [
            { name: "Font Generator", path: "/FontGenerator" },
            { name: "Emoji Generator", path: "/Emojis" },
            { name: "Symbol Generator", path: "/Symbols" },
            { name: "Nickname Generator", path: "/NicknameGenerator" },
            { name: "ASCII Art Generator", path: "/ASCIIArtGenerator" },
          ],
        },
        {
          sub: "Formatting",
          items: [
            { name: "Text Decorator", path: "/TextDecorator" },
            { name: "Instagram Bio Formatter", path: "/InstagramBioFormatter" },
            { name: "Unicode Text Normalizer", path: "/UnicodeTextNormalizer" },
            { name: "Text Formatter", path: "/TextFormatter" },
          ],
        },
      ],
    },
    {
      name: "Text Convert",
      dropdown: [
        {
          sub: "Case & Format",
          items: [
            { name: "Sentence Case Converter", path: "/SentenceCaseConverter" },
            { name: "Reverse Text", path: "/TextReverse" },
            { name: "Find and Replace Text", path: "/FindReplace" },
          ],
        },
        {
          sub: "Data Conversion",
          items: [
            { name: "CSV to JSON Converter", path: "/CsvToJsonConverter" },
            { name: "JSON to Text Converter", path: "/JsonToTextConverter" },
            { name: "Text to Binary Converter", path: "/TextToBinary" },
          ],
        },
        {
          sub: "Ciphers",
          items: [
            { name: "ROT13 Converter", path: "/ROT13Converter" },
            { name: "Caesar Cipher", path: "/CaesarCipher" },
            { name: "Vigenère Cipher", path: "/VigenereCipher" },
          ],
        },
      ],
    },
    {
      name: "Text Analysis",
      dropdown: [
        {
          sub: "Writing Stats",
          items: [
            { name: "Word Counter", path: "/WordCounter" },
            { name: "Letter Frequency Counter", path: "/LetterFrequencyCounter" },
            { name: "Longest and Shortest Word Finder", path: "/LongShortWordFinder" },
            { name: "Character Counter", path: "/CharacterCounter" },
          ],
        },
        {
          sub: "SEO & Readability",
          items: [
            { name: "Keyword Density Checker", path: "/KeywordDensityChecker" },
            { name: "Readability Checker", path: "/ReadabilityChecker" },
            { name: "Sentence Complexity Analyzer", path: "/SentenceComplexityAnalyzer" },
            { name: "Passive Voice Detector", path: "/PassiveVoiceDetector" },
            { name: "SEO Title Length Checker", path: "/SEOTitleLengthChecker" },
          ],
        },
      ],
    },
    {
      name: "Text Cleanup",
      dropdown: [
        {
          sub: "Cleaning Tools",
          items: [
            { name: "Duplicate Line Remover", path: "/DuplicateLineRemover" },
            { name: "Whitespace Cleaner", path: "/WhitespaceRemover" },
            { name: "Remove Line Breaks", path: "/TextLineBreakRemover" },
            { name: "HTML Tag Remover", path: "/HTMLTagRemover" },
          ],
        },
        {
          sub: "Extractors & Dev Tools",
          items: [
            { name: "Unique Words Extractor", path: "/UniqueWordsExtract" },
            { name: "Code Cleaner", path: "/CodeSnippetCleaner" },
            { name: "JSON Schema Generator", path: "/JSONSchemaGenerator" },
            { name: "JavaScript Formatter", path: "/JavaScriptFormatter" },
          ],
        },
      ],
    },
    {
      name: "Encode & Decode",
      dropdown: [
        {
          sub: "Encoding",
          items: [
            { name: "Base64 Encoder / Decoder", path: "/Base64Tool" },
            { name: "URL Encoder / Decoder", path: "/URLEncoderDecoder" },
            { name: "Hex Encoder / Decoder", path: "/HexEncoderDecoder" },
            { name: "Binary Encoder / Decoder", path: "/BinaryEncoderDecoder" },
          ],
        },
        {
          sub: "Identifiers & Utilities",
          items: [
            { name: "UUID Generator", path: "/UUIDGenerator" },
            { name: "JWT Decoder", path: "/JWTDecoder" },
            { name: "Number Extractor", path: "/NumberExtractor" },
            { name: "Morse Code Converter", path: "/MorseCodeConverter" },
          ],
        },
      ],
    },
    {
      name: "Sort & Organize",
      dropdown: [
        {
          sub: "Sorting",
          items: [
            { name: "Alphabetical Sorter", path: "/AlphabeticalSorter" },
            { name: "Reverse Sorter", path: "/ReverseSorter" },
            { name: "Numeric Sorter", path: "/NumericSorter" },
            { name: "Date Sorter", path: "/DateSorter" },
          ],
        },
        {
          sub: "Specialized",
          items: [
            { name: "Domain Sorter", path: "/DomainSorter" },
            { name: "IP Address Sorter", path: "/IPAddressSorter" },
            { name: "Random List Shuffler", path: "/RandomListShuffler" },
            { name: "Password Generator", path: "/PasswordGenerator" },
          ],
        },
      ],
    },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  // Helper function to flatten dropdown items for mobile navigation
  const flattenDropdownItems = (dropdown) => {
    const flattened = [];
    dropdown.forEach(category => {
      category.items.forEach(item => {
        flattened.push(item);
      });
    });
    return flattened;
  };

  return (
    <header className="fm-header" ref={navRef} role="navigation" aria-label="Main navigation">
      <div className="fm-header__container">
        {/* Brand/Logo */}
        <NavLink 
          to="/" 
          className="fm-mobile-menu__brand" 
          onClick={closeMobileMenu}
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <img src={logo} alt="Logo" className="fm-mobile-menu__logo" />
          <span className="fm-mobile-menu__brand-text">Font-Max</span>
        </NavLink> 

        {/* Menu Button */}
        <button 
          className={`fm-header__menu-btn ${mobileOpen ? "fm-header__menu-btn--active" : ""}`}
          onClick={toggleMobile}
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation menu"
          aria-controls="fm-mobile-menu"
        >
          <span className="fm-header__menu-btn-line"></span>
          <span className="fm-header__menu-btn-line"></span>
          <span className="fm-header__menu-btn-line"></span>
          <span className="fm-header__sr-only">Menu</span>
        </button>

        {/* Slide-in Navigation */}
        <nav 
          id="fm-mobile-menu"
          className={`fm-mobile-menu ${mobileOpen ? "fm-mobile-menu--open" : ""}`}
          aria-label="Main navigation"
        >
          <div className="fm-mobile-menu__header">
            <div className="fm-mobile-menu__brand">
              <img src={logo} alt="Logo" className="fm-mobile-menu__logo" />
              <span className="fm-mobile-menu__brand-text">Font-Max</span>
            </div>
          </div>

          <div className="fm-mobile-menu__content">
            {mobileSubMenu === null ? (
              <div className="fm-mobile-menu__main" role="menu">
                <div className="fm-mobile-menu__items">
                  {menuItems.map((item, idx) => (
                    <div key={idx} className="fm-mobile-menu__item" role="none">
                      {item.dropdown ? (
                        <button
                          className="fm-mobile-menu__link fm-mobile-menu__dropdown-btn"
                          onClick={() => openMobileSubMenu(idx)}
                          onKeyDown={(e) => handleMobileKeyDown(e, idx)}
                          role="menuitem"
                          aria-expanded="false"
                        >
                          <span className="fm-mobile-menu__link-text">{item.name}</span>
                          <span className="fm-mobile-menu__arrow" aria-hidden="true">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M4.5 9L7.5 6L4.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        </button>
                      ) : (
                        <NavLink 
                          to={item.path} 
                          className="fm-mobile-menu__link"
                          onClick={closeMobileMenu}
                          role="menuitem"
                          tabIndex={mobileOpen ? 0 : -1}
                        >
                          <span className="fm-mobile-menu__link-text">{item.name}</span>
                        </NavLink>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="fm-mobile-menu__submenu" role="menu">
                <button 
                  className="fm-mobile-menu__back-btn" 
                  onClick={closeMobileSubMenu}
                  role="menuitem"
                  tabIndex={mobileOpen ? 0 : -1}
                >
                  <span className="fm-mobile-menu__back-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10 13L5 8L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="fm-mobile-menu__back-text">Back</span>
                </button>
                
                <div className="fm-mobile-menu__submenu-content">
                  <h3 className="fm-mobile-menu__submenu-title">
                    {menuItems[mobileSubMenu].name}
                  </h3>
                  <div className="fm-mobile-menu__submenu-categories">
                    {menuItems[mobileSubMenu].dropdown.map((category, categoryIdx) => (
                      <div key={categoryIdx} className="fm-mobile-menu__category">
                        <h4 className="fm-mobile-menu__category-title">{category.sub}</h4>
                        <div className="fm-mobile-menu__submenu-items">
                          {category.items.map((link, itemIdx) => (
                            <NavLink
                              key={itemIdx}
                              to={link.path}
                              className={`fm-mobile-menu__submenu-link ${location.pathname === link.path ? 'fm-mobile-menu__submenu-link--active' : ''}`}
                              onClick={closeMobileMenu}
                              role="menuitem"
                              tabIndex={mobileOpen ? 0 : -1}
                            >
                              <span className="fm-mobile-menu__submenu-link-text">{link.name}</span>
                              {location.pathname === link.path && (
                                <span className="fm-mobile-menu__active-indicator" aria-hidden="true">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8L6 11L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </span>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Footer */}
          <div className="fm-mobile-menu__footer">
            <div className="fm-mobile-menu__footer-glow"></div>
            <p className="fm-mobile-menu__footer-text">Professional Text Processing & Formatting Tools</p>
          </div>
        </nav>

        {/* Overlay for mobile menu */}
        <div 
          className={`fm-mobile-menu__overlay ${mobileOpen ? "fm-mobile-menu__overlay--active" : ""}`}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      </div>
    </header>
  );
};

export default NavBar;